import { useCallback, useEffect, useState } from "react"
import { Badge, Button, Col, Row, Spinner, Tab, Tabs } from "react-bootstrap"
import API from "../../common/API"
import CRUD from "../core/genericCRUD/CRUD"

// ─── Types ────────────────────────────────────────────────────────────────────

interface ParteRevisionDTO {
  id: number
  nombre: string | null
  rol: string | null
  identificador: string | null
  cedulaMatchStatus: number
  cedulaMatchCandidates: number | null
  cedulaMatchedAt: string | null
  idAudiencia: number
  nuc: string | null
  tribunal: string | null
  distritoJudicial: string | null
  fechaDate: string | null
  estadoRolAudiencia: string | null
}

interface CandidatoCedulaDTO {
  tipo: string
  identificador: string
  nombre: string | null
  fechaNacimiento: string | null
  sexo: string | null
  lugarNacimiento: string | null
}

interface ServiceStatusDTO {
  pending: number
  matched: number
  ambiguous: number
  notMatched: number
  manual: number
  matchedEntity: number
  ambiguousEntity: number
  preAssigned: number
  total: number
  lastRunAt: string | null
  lastBatch: {
    processed: number
    matched: number
    ambiguous: number
    entity: number
    ambiguousEntity: number
    notMatched: number
    preAssigned: number
    errors: number
  }
  lastError: string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MATCH_STATUS_LABELS: Record<number, { label: string; variant: string }> = {
  0: { label: "Pendiente", variant: "secondary" },
  1: { label: "Matched", variant: "success" },
  2: { label: "Ambiguo", variant: "warning" },
  3: { label: "Sin match", variant: "danger" },
  4: { label: "Manual", variant: "info" },
  5: { label: "Empresa", variant: "success" },
  6: { label: "Empresa ambigua", variant: "warning" },
  7: { label: "Pre-asignado", variant: "primary" },
}

const formatDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("es-DO") : "—"

const MatchBadge = ({ status }: { status: number }) => {
  const s = MATCH_STATUS_LABELS[status] ?? { label: `${status}`, variant: "secondary" }
  return <Badge bg={s.variant}>{s.label}</Badge>
}


// ─── Tab 1: Listado CRUD ──────────────────────────────────────────────────────

const AudienciasTab = () => {
  return (
    <CRUD
      id="expedientes"
      isModal={false}
      filterOpen={true}
      recordButtonsPosition="start"
      title="Expedientes"
      icon="fas fa-gavel"
      getRecordsPath="Audiencias/List"
      getRecordsMethod="post"
      addEditRecordPath=""
      deleteRecordPath=""
      fields={[
        { name: "id", htmlType: "hidden", dbType: "string" },
        { name: "idAudiencia", htmlType: "hidden", dbType: "string" },
        { name: "separator_caso", htmlType: "separator", dbType: "string", title: "Datos del Caso", containerClass: "col-12" },
        { name: "nuc", htmlType: "text", dbType: "string", title: "NUC", containerClass: "col-md-6 col-12", readOnly: true },
        { name: "materia", htmlType: "text", dbType: "string", title: "Materia", containerClass: "col-md-6 col-12", readOnly: true },
        { name: "estadoRolAudiencia", htmlType: "text", dbType: "string", title: "Estado", containerClass: "col-md-4 col-12", readOnly: true },
        { name: "modalidad", htmlType: "text", dbType: "string", title: "Modalidad", containerClass: "col-md-4 col-12", readOnly: true },
        { name: "tipoResultado", htmlType: "text", dbType: "string", title: "Tipo Resultado", containerClass: "col-md-4 col-12", readOnly: true },
        { name: "separator_tribunal", htmlType: "separator", dbType: "string", title: "Tribunal", containerClass: "col-12" },
        { name: "tribunal", htmlType: "text", dbType: "string", title: "Tribunal", containerClass: "col-md-6 col-12", readOnly: true },
        { name: "sala", htmlType: "text", dbType: "string", title: "Sala", containerClass: "col-md-6 col-12", readOnly: true },
        { name: "categoriaTribunal", htmlType: "text", dbType: "string", title: "Categoría", containerClass: "col-md-4 col-12", readOnly: true },
        { name: "distritoJudicial", htmlType: "text", dbType: "string", title: "Distrito Judicial", containerClass: "col-md-4 col-12", readOnly: true },
        { name: "direccionSala", htmlType: "text", dbType: "string", title: "Dirección Sala", containerClass: "col-md-4 col-12", readOnly: true },
        { name: "separator_fecha", htmlType: "separator", dbType: "string", title: "Fecha y Hora", containerClass: "col-12" },
        { name: "fechaDate", htmlType: "text", dbType: "string", title: "Fecha", containerClass: "col-md-4 col-12", readOnly: true },
        { name: "horaAudiencia", htmlType: "text", dbType: "string", title: "Hora", containerClass: "col-md-4 col-12", readOnly: true },
        { name: "urlAudiencia", htmlType: "text", dbType: "string", title: "URL Audiencia", containerClass: "col-md-4 col-12", readOnly: true },
        {
          name: "partes",
          htmlType: "crud",
          dbType: "string",
          title: "Partes",
          containerClass: "col-12",
          getRecordsPath: "Audiencias/ListPartes",

          addEditRecordPath: "",
          deleteRecordPath: "",
          dependOf: "idAudiencia",


          fieldList: [
            { name: "nombre", htmlType: "text", dbType: "string", title: "Nombre", containerClass: "col-12 col-md-4" },
            { name: "rol", htmlType: "text", dbType: "string", title: "Rol", containerClass: "col-12 col-md-3" },
            { name: "identificador", htmlType: "text", dbType: "string", title: "Identificador", containerClass: "col-12 col-md-3" },
            { name: "cedulaMatchStatus", htmlType: "text", dbType: "string", title: "Match", containerClass: "col-12 col-md-2" },
          ],
          subItems: [
            { name: "id", htmlType: "hidden", dbType: "string" },
            { name: "idAudiencia", htmlType: "hidden", dbType: "number" },
            { name: "nombre", htmlType: "text", dbType: "string", title: "Nombre", containerClass: "col-12 col-md-6", readOnly: true },
            { name: "rol", htmlType: "text", dbType: "string", title: "Rol", containerClass: "col-12 col-md-6", readOnly: true },
            { name: "identificador", htmlType: "text", dbType: "string", title: "Identificador", containerClass: "col-12 col-md-4", readOnly: true },
            { name: "cedulaMatchStatus", htmlType: "text", dbType: "string", title: "Estado Match", containerClass: "col-12 col-md-4", readOnly: true },
            { name: "cedulaMatchCandidates", htmlType: "text", dbType: "string", title: "Candidatos", containerClass: "col-12 col-md-4", readOnly: true },
            { name: "cedulaMatchedAt", htmlType: "text", dbType: "string", title: "Fecha Match", containerClass: "col-12 col-md-6", readOnly: true },
          ],
        },
      ]}
      filterFields={[
        {
          name: "cedula",
          htmlType: "text",
          dbType: "string",
          title: "Cédula / Identificador",
          containerClass: "col-md-3 col-12",
          required: false,
        },
        {
          name: "nuc",
          htmlType: "text",
          dbType: "string",
          title: "NUC",
          containerClass: "col-md-3 col-12",
          required: false,
        },
        {
          name: "tribunal",
          htmlType: "text",
          dbType: "string",
          title: "Tribunal",
          containerClass: "col-md-3 col-12",
          required: false,
        },
        {
          name: "estadoRolAudiencia",
          htmlType: "select",
          dbType: "string",
          title: "Estado",
          containerClass: "col-md-3 col-12",
          required: false,
          dataSourceLocal: [
            { id: "", name: "Todos" },
            { id: "Pendiente", name: "Pendiente" },
            { id: "Conocida", name: "Conocida" },
          ],
          dataSourceTextField: "name",
          dataSourceValueField: "id",
        },
      ]}
      fieldsList={[
        { name: "fechaDate", title: "Fecha", htmlType: "date" },
        { name: "nuc", title: "NUC", htmlType: "text" },
        { name: "tribunal", title: "Tribunal", htmlType: "text" },
        { name: "sala", title: "Sala", htmlType: "text" },
        { name: "distritoJudicial", title: "Distrito Judicial", htmlType: "text" },
        { name: "materia", title: "Materia", htmlType: "text" },
        { name: "estadoRolAudiencia", title: "Estado", htmlType: "text" },
      ]}
    />
  )
}



// ─── Contenedor principal ─────────────────────────────────────────────────────

const AdminExpedientesComponent = () => {
  return (
    <div>
      <h5 className="mb-3">
        <i className="fas fa-gavel me-2 text-primary" />
        Expedientes — Audiencias y Partes
      </h5>
      <AudienciasTab />
    </div>
  )
}

export default AdminExpedientesComponent
